import { size, find, first } from 'lodash-es'
import { useMutation } from '@apollo/client/react'
import { useForm } from 'react-hook-form'
import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import Router, { useRouter } from 'next/router'
import { Stack, Typography } from '@mui/material'
import Head from 'next/head'
import { Button } from 'settle-ui/components/Button/Button'
import { GoogleAuth, type GoogleCredential } from 'settle-ui/components/GoogleAuth/GoogleAuth'
import { DividersBlock } from 'settle-ui/components/DividersBlock/DividersBlock'
import { Section } from 'settle-ui/components/Section/Section'
import { SectionItem } from 'settle-ui/components/SectionItem/SectionItem'
import { TextFieldController } from 'settle-ui/form/TextFieldController/TextFieldController'
import { PasswordInputController } from 'settle-ui/form/PasswordInputController/PasswordInputController'
import { useSnackbars } from 'settle-ui/hooks/useSnackbars/useSnackbars'
import { AuthPageLayout } from 'routes/components/AuthPageLayout/AuthPageLayout'
import { AuthCard } from 'routes/components/AuthCard/AuthCard'
import { AuthActionsRow } from 'routes/components/AuthActionsRow/AuthActionsRow'
import { externalLinks } from 'lib/consts/externalLinks'
import { logEvent } from 'lib/tracking/segment'
import { RouterPage } from 'lib/navigation/consts'
import { isNextUrlValid } from 'lib/helpers/isNextUrlValid/isNextUrlValid'
import { EMAIL_PATTERN } from 'lib/patterns'
import { useBrandName } from 'lib/hooks/useBrandName/useBrandName'
import { qsStringSchema } from 'lib/zod'
import { useDeleteTokenCookieOnMount } from 'lib/hooks/useDeleteTokenCookieOnMount/useDeleteTokenCookieOnMount'
import { clearStoredBusinessId, getStoredBusinessId } from 'lib/helpers/businessIdStorage/businessIdStorage'
import { getClientManagerClientsPath } from 'lib/navigation/helpers/getClientManagerClientsPath/getClientManagerClientsPath'
import { getBusinessRootPath } from 'lib/navigation/helpers/getBusinessRootPath/getBusinessRootPath'
import { isClientManagerRole } from 'routes/helpers/isClientManagerRole/isClientManagerRole'
import { withPageErrorRecovery } from 'lib/hocs/withPageErrorRecovery/withPageErrorRecovery'
import { isApolloErrorWithCode } from 'lib/helpers/isApolloErrorWithCode/isApolloErrorWithCode'
import { useAuthResponseHandler } from 'routes/hooks/useAuthResponseHandler/useAuthResponseHandler'
import { MetaTags } from '../components/MetaTags/MetaTags'
import { useUrlBasedSnackbar } from '../hooks/useUrlBasedSnackbar/useUrlBasedSnackbar'
import { useExistingVendorWarning } from './hooks/useExistingVendorWarning/useExistingVendorWarning'
import * as events from './LoginPage.events'
import {
  type LoginPage_ProfileFragment,
  LoginPage_signInDocument,
  LoginPage_authProviderSignInDocument,
} from './LoginPage.graphql'
import { CreateAccountButton } from './components/CreateAccountButton/CreateAccountButton'
import { ForgotPasswordLink } from './components/ForgotPasswordLink/ForgotPasswordLink'
import { useReCaptcha } from './hooks/useReCaptcha/useReCaptcha'

export interface LoginForm {
  email: string
  password: string
}

export const LoginPage = withPageErrorRecovery(() => {
  useExistingVendorWarning()
  useUrlBasedSnackbar({ permanent: true })
  const reCaptcha = useReCaptcha()
  const brand = useBrandName()
  const router = useRouter()

  const next = qsStringSchema.parse(router.query.next)
  const email = qsStringSchema.parse(router.query.email)

  const { t } = useTranslation()
  const { showApolloOrTextError, showError } = useSnackbars()

  const [signIn] = useMutation(LoginPage_signInDocument)
  const [signInGoogle] = useMutation(LoginPage_authProviderSignInDocument)
  const [isLoading, setIsLoading] = useState(false)

  const form = useForm<LoginForm>({ defaultValues: { email, password: '' } })
  const formEmail = form.watch('email')
  const { reset } = form

  useDeleteTokenCookieOnMount()

  useEffect(() => {
    reset({ email })
  }, [email, reset])

  const onSuccess = (profile: Maybe<LoginPage_ProfileFragment>) => {
    const next = new URLSearchParams(document.location.search).get('next')

    if (next && isNextUrlValid(next)) {
      return document.location.assign(next)
    }

    const { actions, memberships = [], accountingFirms = [] } = profile ?? {}

    if (size(memberships) === 0) {
      setIsLoading(false)
      showError(t('noBusinessLoginErrorMessage'))
      return null
    }

    const businessId = getStoredBusinessId()

    if (actions?.viewAccountantDashboard?.allowed) {
      const clientManagerBusiness =
        find(accountingFirms, (business) => business.id === businessId) || first(accountingFirms)

      if (clientManagerBusiness) {
        const { role } = find(memberships, ({ business }) => business?.id === clientManagerBusiness.id) ?? {}
        const hasAccessibleParentBusinessRole = role && !isClientManagerRole(role)

        return Router.push({
          pathname: getClientManagerClientsPath(clientManagerBusiness.id),
          ...(hasAccessibleParentBusinessRole && {
            query: {
              back_path: getBusinessRootPath(clientManagerBusiness.id),
              back_business_name: clientManagerBusiness.officialName,
            },
          }),
        })
      }
    }

    if (businessId && memberships.find(({ business }) => business?.id === businessId)) {
      return Router.push(getBusinessRootPath(businessId))
    }

    clearStoredBusinessId()

    return Router.push(RouterPage.root)
  }

  const handleAuthResponse = useAuthResponseHandler()

  const handleGoogleSuccess = async (jwt: string, credential: GoogleCredential) => {
    try {
      setIsLoading(true)

      const { data } = await signInGoogle({ variables: { data: jwt } })

      const handled = await handleAuthResponse(
        { authResponse: data?.authProviderSignIn, email: credential.email, next },
        {
          onAuthenticated: (profile) => {
            void onSuccess(profile)
            setTimeout(() => google.accounts.id.revoke(credential.email), 1000)
          },
        },
      )

      if (!handled) {
        setIsLoading(false)
        showError(t('failedToSignIn'))
      }
    } catch (err) {
      google.accounts.id.revoke(credential.email)
      setIsLoading(false)
      showApolloOrTextError(err, t('failedToSignIn'), { 'data-testid': 'login-page_apollo_error' })
    }
  }

  const handleSubmit = async ({ email, password }: LoginForm) => {
    void logEvent(events.LOGIN_BUTTON_CLICK)

    try {
      setIsLoading(true)

      const result = await reCaptcha.verify('LOGIN')
      const { data } = await signIn({
        variables: { email, password },
        context: {
          headers: result,
        },
      })

      const signInResult = data?.signIn

      const handled = await handleAuthResponse(
        { authResponse: signInResult, email, next },
        { onAuthenticated: onSuccess },
      )

      if (!handled) {
        setIsLoading(false)
        showError(t('failedToSignIn'))
      }
    } catch (err) {
      setIsLoading(false)

      if (isApolloErrorWithCode(err, { code: 'account_locked' })) {
        showApolloOrTextError(err, t('failedToSignIn'), {
          text: t('contactSupport'),
          onClick: () => (window.location.href = `mailto:${externalLinks.supportEmail}`),
        })
        return
      }

      showApolloOrTextError(err, t('failedToSignIn'))
    }
  }

  return (
    <>
      <Head>
        <MetaTags title={brand} description={t('loginPage::meta::description')} />
      </Head>
      <AuthPageLayout>
        <AuthCard fullWidth={true}>
          <Section fullWidth={true}>
            <SectionItem fullWidth={true} horizontalGutter="none" verticalGutter="none">
              <Typography
                variant="h5"
                color="onSurfacePrimary"
                sx={{
                  fontWeight: 700,
                  textAlign: 'center',
                }}
              >
                {t('login')}
              </Typography>
            </SectionItem>
          </Section>
          <Stack
            component="form"
            noValidate={true}
            method="post"
            onSubmit={form.handleSubmit(handleSubmit)}
            sx={{ flex: 1 }}
          >
            <Section fullWidth={true}>
              <SectionItem fullWidth={true} horizontalGutter="none" sx={{ alignItems: 'center' }}>
                <GoogleAuth onSuccess={handleGoogleSuccess} />
              </SectionItem>
              <SectionItem fullWidth={true} horizontalGutter="none">
                <DividersBlock>
                  <Typography variant="caption" color="onSurfaceSecondary" sx={{ textTransform: 'uppercase' }}>
                    {t('or')}
                  </Typography>
                </DividersBlock>
              </SectionItem>
              <SectionItem fullWidth={true} horizontalGutter="none">
                <TextFieldController
                  margin="none"
                  color="secondary"
                  type="email"
                  name="email"
                  data-testid="email-field"
                  control={form.control}
                  label="Email"
                  autoComplete="username"
                  rules={{
                    pattern: { value: EMAIL_PATTERN, message: t('emailIsIncorrect') },
                    required: { value: true, message: t('emailCantBeEmpty') },
                  }}
                />
              </SectionItem>
              <SectionItem fullWidth={true} horizontalGutter="none">
                <PasswordInputController
                  margin="none"
                  color="secondary"
                  name="password"
                  data-testid="password-field"
                  control={form.control}
                  label="Password"
                  autoComplete="current-password"
                  rules={{
                    required: { value: true, message: t('passwordCantBeEmpty') },
                  }}
                />
              </SectionItem>
            </Section>
            <Section fullWidth={true} sx={{ marginTop: 'auto', pb: 0 }}>
              <AuthActionsRow direction={{ xs: 'column', md: 'row' }}>
                <CreateAccountButton />
                <Button
                  type="submit"
                  variant="primary"
                  loading={isLoading}
                  disabled={!reCaptcha.ready}
                  fullWidth={true}
                >
                  {t('logIn')}
                </Button>
              </AuthActionsRow>
              <SectionItem fullWidth={true} horizontalGutter="none">
                <ForgotPasswordLink email={formEmail} />
              </SectionItem>
            </Section>
          </Stack>
        </AuthCard>
      </AuthPageLayout>
    </>
  )
})
